import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Wrench,
  Plus,
  Search,
  ChevronRight,
  ChevronLeft,
  Loader2,
  RefreshCw
} from 'lucide-react';

// Import refactored components
import {
  ToolCard,
  ToolFormDialog,
  SyncDialog,
  TestResultDialog,
  ExecutionLogsDialog,
  API_URL,
  HTTP_METHODS,
  CATEGORIES,
  DEFAULT_FORM_DATA
} from '../components/agent-tools';

export const AgentToolsPage = () => {
  const { t } = useTranslation('workflows');
  // List state
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dynamic filter options
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    methods: [],
    statuses: []
  });
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [bodyJsonString, setBodyJsonString] = useState('{}');
  const [testPayloadString, setTestPayloadString] = useState('{}');
  
  // Test state
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  
  // Logs state
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // Sync state
  const [syncLoading, setSyncLoading] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchTools();
  }, [page, statusFilter, categoryFilter, methodFilter]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/agent-tools/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchTools = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (methodFilter !== 'all') params.append('method', methodFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/agent-tools?${params}`);
      setTools(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Error al cargar herramientas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchTools();
  };

  const openForm = async (tool = null) => {
    if (tool) {
      try {
        const response = await axios.get(`${API_URL}/api/agent-tools/${tool.id}`);
        const fullTool = response.data;
        setEditingTool(fullTool);
        setFormData({
          ...DEFAULT_FORM_DATA,
          ...fullTool,
          auth: { ...DEFAULT_FORM_DATA.auth, ...fullTool.auth }
        });
        setBodyJsonString(JSON.stringify(fullTool.body_json || {}, null, 2));
        setTestPayloadString(JSON.stringify(fullTool.test_payload || {}, null, 2));
      } catch (error) {
        toast.error('Error al cargar herramienta');
        return;
      }
    } else {
      setEditingTool(null);
      setFormData(DEFAULT_FORM_DATA);
      setBodyJsonString('{}');
      setTestPayloadString('{}');
    }
    setTestResult(null);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.base_url) {
      toast.error('Nombre y URL base son requeridos');
      return;
    }
    
    let bodyJson = {};
    let testPayload = {};
    try {
      bodyJson = bodyJsonString ? JSON.parse(bodyJsonString) : {};
    } catch (e) {
      toast.error('Body JSON inválido');
      return;
    }
    try {
      testPayload = testPayloadString ? JSON.parse(testPayloadString) : {};
    } catch (e) {
      toast.error('Test Payload JSON inválido');
      return;
    }
    
    const submitData = {
      ...formData,
      body_json: bodyJson,
      test_payload: testPayload
    };
    
    setFormLoading(true);
    try {
      if (editingTool) {
        await axios.put(`${API_URL}/api/agent-tools/${editingTool.id}`, submitData);
        toast.success('Herramienta actualizada');
      } else {
        await axios.post(`${API_URL}/api/agent-tools`, submitData);
        toast.success('Herramienta creada');
      }
      setShowForm(false);
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando herramienta');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (toolId) => {
    if (!window.confirm('¿Eliminar esta herramienta?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/agent-tools/${toolId}`);
      toast.success('Herramienta eliminada');
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando herramienta');
    }
  };

  const handleDuplicate = async (toolId) => {
    try {
      await axios.post(`${API_URL}/api/agent-tools/${toolId}/duplicate`);
      toast.success('Herramienta duplicada');
      fetchTools();
    } catch (error) {
      toast.error('Error duplicando herramienta');
    }
  };

  const handleTest = async () => {
    if (!editingTool?.id) {
      toast.error('Guarda la herramienta primero');
      return;
    }
    
    let testPayload = {};
    try {
      testPayload = testPayloadString ? JSON.parse(testPayloadString) : {};
    } catch (e) {
      toast.error('Test Payload JSON inválido');
      return;
    }
    
    setTestLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/agent-tools/${editingTool.id}/test`,
        testPayload
      );
      setTestResult(response.data);
      setShowTestDialog(true);
    } catch (error) {
      toast.error('Error ejecutando prueba');
    } finally {
      setTestLoading(false);
    }
  };

  const fetchLogs = async (toolId) => {
    setLogsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/agent-tools/${toolId}/logs`);
      setLogs(response.data.data);
      setShowLogsDialog(true);
    } catch (error) {
      toast.error('Error cargando logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSyncPreview = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const response = await axios.get(`${API_URL}/api/agent-tools/sync/preview`);
      setSyncPreview(response.data);
      setShowSyncDialog(true);
    } catch (error) {
      toast.error('Error obteniendo preview de sincronización');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/agent-tools/sync`);
      setSyncResult(response.data);
      toast.success(response.data.message);
      fetchTools();
      fetchFilterOptions(); // Refresh filter options after sync
    } catch (error) {
      toast.error('Error sincronizando APIs');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleClearSynced = async () => {
    if (!window.confirm('¿Eliminar todas las herramientas sincronizadas del sistema?')) return;
    
    setSyncLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/agent-tools/sync/clear`);
      toast.success(response.data.message);
      setShowSyncDialog(false);
      fetchTools();
      fetchFilterOptions(); // Refresh filter options after clear
    } catch (error) {
      toast.error('Error eliminando herramientas sincronizadas');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleToggleStatus = async (toolId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/agent-tools/${toolId}`, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Herramienta activada' : 'Herramienta desactivada');
      fetchTools();
    } catch (error) {
      toast.error('Error actualizando estado');
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-tools-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Herramientas del Agente</h1>
        <p className="text-gray-500 mt-1">Configura las herramientas externas que el agente IA puede utilizar</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar herramientas..."
              className="pl-10"
              data-testid="tool-search"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {filterOptions.statuses.map(s => {
              const statusLabels = {
                active: 'Activo',
                inactive: 'Inactivo',
                draft: 'Borrador'
              };
              return (
                <SelectItem key={s.code} value={s.code}>
                  {statusLabels[s.code] || s.code}
                  {s.count > 0 && <span className="ml-2 text-xs text-gray-400">({s.count})</span>}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {filterOptions.categories.map(cat => (
              <SelectItem key={cat.code} value={cat.code}>
                {cat.code === 'internal_api' ? 'API Interna' : 
                 cat.code === 'external_api' ? 'API Externa' : 
                 cat.code === 'messaging' ? 'Mensajería' :
                 cat.code === 'notification' ? 'Notificaciones' :
                 cat.code === 'email' ? 'Email' :
                 cat.code === 'sms' ? 'SMS' :
                 cat.code === 'webhook' ? 'Webhook' :
                 cat.code === 'ai' ? 'IA/ML' :
                 cat.code === 'crm' ? 'CRM' : cat.code}
                <span className="ml-2 text-xs text-gray-400">({cat.count})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={(v) => { setMethodFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Método" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {filterOptions.methods.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => openForm()} className="bg-[#E63946] hover:bg-[#d32836]" data-testid="new-tool-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Herramienta
        </Button>
        <Button 
          variant="outline" 
          onClick={handleSyncPreview}
          disabled={syncLoading}
          data-testid="sync-tools-btn"
        >
          {syncLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Sincronizar APIs
        </Button>
      </div>

      {/* Tools Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : tools.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onEdit={openForm}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onViewLogs={fetchLogs}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Wrench className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No hay herramientas configuradas</p>
          <p className="text-sm mt-2">Crea herramientas para que el agente IA pueda interactuar con APIs externas</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4 text-sm">Página {page} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Tool Form Dialog */}
      <ToolFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editingTool={editingTool}
        formData={formData}
        setFormData={setFormData}
        bodyJsonString={bodyJsonString}
        setBodyJsonString={setBodyJsonString}
        testPayloadString={testPayloadString}
        setTestPayloadString={setTestPayloadString}
        formLoading={formLoading}
        testLoading={testLoading}
        onSubmit={handleSubmit}
        onTest={handleTest}
        onCancel={() => setShowForm(false)}
      />

      {/* Test Result Dialog */}
      <TestResultDialog
        open={showTestDialog}
        onOpenChange={setShowTestDialog}
        testResult={testResult}
      />

      {/* Execution Logs Dialog */}
      <ExecutionLogsDialog
        open={showLogsDialog}
        onOpenChange={setShowLogsDialog}
        logs={logs}
        loading={logsLoading}
      />

      {/* Sync Dialog */}
      <SyncDialog
        open={showSyncDialog}
        onOpenChange={setShowSyncDialog}
        syncPreview={syncPreview}
        syncResult={syncResult}
        syncLoading={syncLoading}
        onSync={handleSync}
        onClear={handleClearSynced}
        onClose={() => { setShowSyncDialog(false); setSyncResult(null); }}
      />
    </div>
  );
};

export default AgentToolsPage;
